function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { white, grey2, lightBlue, blueWash, blue, grey6, black30, blueWashAlt, black70, grey60, grey40, blueHover, blueActive, line, grey6Hover, green, greenHover, greenLight, red, negativeForeground, yellow, redHeavy, greyDark, grey15, blue20, blue5, bgOverlay, white70, grey30, white25, white40, white60, grey90, blue10, black0, white0, transparent, toCssVar } from './colors.js';
import deprecatedColors from './deprecatedColors.js';
import zIndex from './zIndex.js';

/**
 * Shared theme config for all of the workspaces. This allows us to maintain a
 * consistent visual experience across projects. For changes that only
 * need to modify a single app, override this base theme and pass the
 * modified version into your root themeProvider
 */

const size = {
  tiny: '2px',
  small: '4px',
  medium: '8px',
  large: '16px',
  huge: '24px',
  enormous: '32px'
};
const fontSize = {
  tinier: '10px',
  tiny: '12px',
  small: '14px',
  medium: '16px',
  large: '18px',
  huge: '24px',
  super: '32px',
  mega: '48px'
};

// TODO drop breakpoints and replace with devices.ts
const breakpoints = {
  giant: 1170,
  desktop: 992,
  tablet: 1040,
  phone: 768,
  phone_small: 560
};
const fontWeight = {
  ultraLight: 400,
  thin: 400,
  light: 400,
  regular: 400,
  medium: 400,
  demiBold: 500,
  bold: 500,
  heavy: 500
};
const fallbackFonts = `-apple-system,
BlinkMacSystemFont,
"Segoe UI",
"Roboto",
"Oxygen",
"Ubuntu",
"Cantarell",
"Fira Sans",
"Droid Sans",
"Helvetica Neue",
sans-serif`;
const font = {
  fontFamily: `'CoinbaseSans', ${fallbackFonts}`,
  sans: `'CoinbaseSans', ${fallbackFonts}`,
  text: `'CoinbaseText', ${fallbackFonts}`,
  display: `'CoinbaseDisplay', ${fallbackFonts}`
};
const monospaceFont = {
  fontFamily: `
  "Monaco",
  monospace;`
};
const fontFamily = {
  regular: font.fontFamily,
  sans: font.sans,
  text: font.text,
  display: font.display,
  monospace: monospaceFont.fontFamily
};
const color = {
  background: white,
  backgroundHover: grey2,
  backgroundSelected: grey2,
  backgroundActive: lightBlue,
  backgroundPill: blueWash,
  // TEMP for ease of remapping: removed after blue bg theme added
  backgroundAccent: blue,
  // TEMP remapping all colors to CDS spectrum
  backgroundAlt: white,
  backgroundHoverAlt: grey6,
  backgroundAccentAlt: black30,
  backgroundWash: blueWashAlt,
  // Text
  text: black70,
  textLight: grey60,
  textLightAlt: grey40,
  textAccent: blue,
  textAccentHover: blueHover,
  textAccentActive: blueActive,
  // Borders
  border: line,
  borderHover: grey6Hover,
  borderAccent: blue,
  borderAccentHover: blueActive,
  // Buttons
  buttonFill: blue,
  buttonFillHover: blueHover,
  buttonFillActive: blueActive,
  buttonOutline: grey6,
  buttonOutlineHover: blue,
  buttonOutlineActive: blue,
  // Percent Changes + Errors
  positive: green,
  positiveHover: greenHover,
  positiveLight: greenLight,
  negative: red,
  negativeForeground: negativeForeground,
  warning: yellow,
  // Icons
  iconBackground: grey6,
  iconFill: blue,
  iconBackgroundAccent: blue,
  iconFillAccent: white,
  iconLight: grey60,
  iconWarning: redHeavy,
  iconGreyDark: greyDark,
  iconGreyDarkAlt: grey15,
  iconBlue: blue20,
  iconBlueBorder: blue5,
  // Ui
  placeholder: grey40,
  disabled: grey2,
  boxShadow: grey2,
  maskDark: bgOverlay,
  maskLight: white70,
  maskBlue: 'rgba(26,54,80,0.7)',
  // Flourishes - tabs, chevrons, pills, hamburger menu etc.
  accessoryActive: blue,
  accessoryInactive: grey30,
  accessoryBackground: grey2,
  // Third parties
  twitter: '#499FEC',
  facebook: '#415894',
  /* Misc. */
  white: white,
  white25: white25,
  white40: white40,
  white60: white60,
  grey90: grey90,
  blue10: blue10,
  /* Transparent */
  black0: black0,
  // For animating to black
  white0: white0,
  // For animating to white
  transparent: transparent,
  // ------------------------------------
  // Colors under this line will be removed after
  // '2019.january.web_design_system' has been resolved

  /* Dark Grays */
  slate: grey60,
  slateDark: black70,
  cornFlowerBlue: blue,
  catalinaBlue: blue,
  heather: grey6,
  midnightBlue: blue,
  /* Misc. */
  black: black70
};
const border = {
  border: `1px solid ${line}`,
  noRadius: '0px',
  subtleRadius: '2px',
  borderRadius: `${size.small}`,
  mediumBorderRadius: `${size.medium}`,
  largeBorderRadius: `${size.large}`,
  radius: `${size.small}`,
  insetRadius: '3px'
};
const boxShadow = {
  default: `0 4px 12px 0 ${color.boxShadow}`,
  hover: `0 2px 5px ${toCssVar(['gray100', 0.08])}`,
  deep: `0 4px 16px ${toCssVar(['blue100', 0.05])}`,
  light: `0px 8px 16px ${toCssVar(['gray5', 0.5])}`
};
/*
These are Earn specific styles
The Earn designer (Matt Storus) gave very specific styles which differ slightly
from the styles used by the rest of the site

The style and design doc is here:
https://paper.dropbox.com/doc/Earn-Product-Spec--AQ0P9w3fawnwhGSo5ebgs3HtAg-BZ6dsgQGmXV7G84rDuLYI

This theme will change per asset issuer later and may need to be moved
into a separate file.
*/

const airdrop = {
  color: {
    grey5: '#F5F7F8',
    grey10: '#E7EBEE',
    grey30: '#B8C2CC',
    grey50: '#8899AA',
    grey60: toCssVar('gray60'),
    grey70: '#587088',
    grey80: '#113355',
    background: '#F5F7F8'
  },
  spacing: {
    tiny: '6px',
    small: '12px',
    medium: '24px',
    large: '36px',
    xl: '48px',
    xxl: '96px'
  },
  pageBackgroundWidth: 1440,
  pageContentPadding: 24,
  borderRadius: '7.68px',
  fontFamily: `CoinbaseSans-Medium,CoinbaseSans,'Avenir Next',-apple-system,BlinkMacSystemFont,"Segoe UI","Roboto","Oxygen","Ubuntu","Cantarell","Fira Sans","Droid Sans","Helvetica Neue",sans-serif;`
};
const layout = {
  width: 1180,
  padding: 24
};
const theme = {
  typeVariant: false,
  // temporary variant, will be removed post design system launch
  airdrop,
  size,
  fontSize,
  fontWeight,
  fontFamily,
  color: _objectSpread(_objectSpread({}, deprecatedColors), color),
  border,
  boxShadow,
  layout,
  breakpoints,
  zIndex,
  _snapshotDisplayName: 'defaultTheme'
};
export { font, theme };
//# sourceMappingURL=theme.js.map
